/**
 * DeepAI theme feature — constants.
 *
 * A theme is nothing more than a block of CSS custom properties applied on
 * `html[data-theme="<id>"]`. Components consume the variables, so switching a
 * theme is just swapping the `data-theme` attribute. The token values here are
 * the source of truth mirrored from the design prototype at
 * `public/demo/ai-app/index.html`.
 */
/**
 * Redux store subtree name for this feature.
 */
export const STORE_NAME = 'features/deepai-theme';
/**
 * Id of the injected <style> element holding all theme variable blocks.
 */
export const THEME_STYLE_ELEMENT_ID = 'deepai-theme-vars';
/**
 * The selectable themes (value = data-theme id, label = Japanese UI label).
 * The empty value restores the app's default look (no data-theme attribute).
 */
export const DEEPAI_THEMES = [
    { value: 'cool', label: 'クール' },
    { value: 'minimal', label: 'ミニマル' },
    { value: 'lux', label: 'ラグジュアリー' },
    { value: 'natural', label: 'ナチュラル' },
    { value: 'pop', label: 'ポップ' },
    { value: 'cute', label: 'キュート' },
    { value: 'clay', label: 'クレイ' },
    { value: 'mono', label: 'モノクロ' },
    { value: 'neon', label: 'ネオン' },
    { value: 'brutal', label: 'ブルータル' },
    { value: 'wa', label: '和モダン' },
    { value: 'crt', label: 'レトロCRT' },
    { value: 'water', label: 'ウォーター' },
    { value: 'ocean', label: 'オーシャン' }
];
/**
 * Default theme id. Defaults to the pop theme (the デフォルト/no-theme option was
 * removed from the picker).
 */
export const DEFAULT_THEME = 'pop';
/**
 * The full stylesheet: token definitions per theme, plus a small set of applied
 * rules (only active when a theme is selected) so the switch is immediately
 * visible on the main surfaces. Deeper per-surface re-skin is layered on later.
 */
export const THEME_STYLE = `
:root{
  --bg:
    radial-gradient(1200px 800px at 78% -10%, rgba(60,90,200,0.35), transparent 60%),
    radial-gradient(1000px 700px at 8% 110%, rgba(40,70,180,0.30), transparent 55%),
    linear-gradient(160deg, #070b1c 0%, #0a1030 45%, #0b1338 100%);
  --surface:rgba(20,28,54,0.55);
  --surface-2:rgba(28,38,72,0.42);
  --border:rgba(140,165,230,0.20);
  --text:#eaf0ff; --text-dim:#93a0c4;
  --accent:#246FE5; --accent-2:#5fe6ff; --on-accent:#ffffff;
  --danger:#D83848;
  --radius:22px; --radius-sm:14px;
  --font:-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue","Hiragino Kaku Gothic ProN","Hiragino Sans","Yu Gothic UI","Meiryo",system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 30px 80px -30px rgba(0,0,0,0.7);
  --glow:rgba(90,150,255,0.42);
  --wave-1:#246FE5; --wave-2:#5fe6ff;
}
:root[data-theme="cool"]{
  --bg:
    radial-gradient(1200px 800px at 78% -10%, rgba(60,90,200,0.35), transparent 60%),
    radial-gradient(1000px 700px at 8% 110%, rgba(40,70,180,0.30), transparent 55%),
    linear-gradient(160deg, #070b1c 0%, #0a1030 45%, #0b1338 100%);
  --surface:rgba(20,28,54,0.55);
  --surface-2:rgba(28,38,72,0.42);
  --border:rgba(140,165,230,0.20);
  --text:#eaf0ff; --text-dim:#93a0c4;
  --accent:#246FE5; --accent-2:#5fe6ff; --on-accent:#fff;
  --danger:#D83848;
  --radius:22px; --radius-sm:14px;
  --font:-apple-system,BlinkMacSystemFont,"Segoe UI","Hiragino Kaku Gothic ProN","Hiragino Sans","Yu Gothic UI",Meiryo,system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 30px 80px -30px rgba(0,0,0,0.7);
  --glow:rgba(90,150,255,0.42);
  --wave-1:#246FE5; --wave-2:#5fe6ff;
}
:root[data-theme="minimal"]{
  --bg:#F7F8FA;
  --surface:#FFFFFF; --surface-2:#F0F2F4; --border:#E6E9ED;
  --text:#1A1D22; --text-dim:#5B6470;
  --accent:#246FE5; --accent-2:#4d8bff; --on-accent:#fff;
  --danger:#D83848;
  --radius:20px; --radius-sm:14px;
  --font:-apple-system,BlinkMacSystemFont,"Segoe UI","Hiragino Kaku Gothic ProN","Hiragino Sans","Yu Gothic UI",Meiryo,sans-serif;
  --font-head:var(--font);
  --shadow:0 1px 2px rgba(16,24,40,.05), 0 12px 30px rgba(16,24,40,.07);
  --glow:rgba(36,111,229,0.14);
  --wave-1:#246FE5; --wave-2:#78aaff;
}
:root[data-theme="lux"]{
  --bg:
    radial-gradient(120% 90% at 82% -10%, rgba(14,42,34,.55), transparent 60%),
    radial-gradient(100% 80% at 0% 110%, rgba(14,42,34,.35), transparent 55%),
    #0a0d0c;
  --surface:#0d1a16; --surface-2:#0b1f19; --border:rgba(200,168,96,.30);
  --text:#e8e5db; --text-dim:#9aa39b;
  --accent:#c8a860; --accent-2:#d8be78; --on-accent:#0a0d0c;
  --danger:#c05648;
  --radius:6px; --radius-sm:4px;
  --font:"Helvetica Neue","Hiragino Sans","Yu Gothic",Arial,sans-serif;
  --font-head:Georgia,"Yu Mincho","Hiragino Mincho ProN","Times New Roman",serif;
  --shadow:0 30px 70px -30px rgba(0,0,0,0.8);
  --glow:rgba(200,168,96,0.24);
  --wave-1:#c8a860; --wave-2:#d8be78;
}
:root[data-theme="natural"]{
  --bg:
    radial-gradient(1200px 700px at 80% -10%, #f1ead9 0%, transparent 60%),
    radial-gradient(900px 600px at -10% 110%, #efe6d4 0%, transparent 55%),
    #f6f1e7;
  --surface:#fffdf8; --surface-2:#efe7d7; --border:#e4dccb;
  --text:#3a352c; --text-dim:#6b6455;
  --accent:#8a9a6f; --accent-2:#c98a68; --on-accent:#fff;
  --danger:#b06a4c;
  --radius:24px; --radius-sm:16px;
  --font:-apple-system,BlinkMacSystemFont,"Hiragino Kaku Gothic ProN","Yu Gothic",Meiryo,sans-serif;
  --font-head:Georgia,"Hiragino Mincho ProN","Yu Mincho",serif;
  --shadow:0 18px 40px -18px rgba(90,80,60,.35);
  --glow:rgba(138,154,111,0.22);
  --wave-1:#8a9a6f; --wave-2:#c98a68;
}
:root[data-theme="pop"]{
  --bg:
    radial-gradient(1000px 520px at 88% -8%, rgba(139,92,246,.16), transparent 60%),
    radial-gradient(900px 480px at -6% 108%, rgba(47,211,165,.16), transparent 60%),
    radial-gradient(760px 420px at 106% 96%, rgba(255,107,107,.14), transparent 60%),
    #F3F6FF;
  --surface:#FFFFFF; --surface-2:#EEF1FA; --border:#E7EAF4;
  --text:#1B2340; --text-dim:#5A6485;
  --accent:#8B5CF6; --accent-2:#2FD3A5; --on-accent:#fff;
  --danger:#FF5A6B;
  --radius:28px; --radius-sm:18px;
  --font:"Hiragino Maru Gothic ProN","Hiragino Kaku Gothic ProN","Yu Gothic UI","Noto Sans JP",system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 18px 40px -18px rgba(139,92,246,.30);
  --glow:rgba(139,92,246,0.18);
  --wave-1:#8B5CF6; --wave-2:#FF6B6B;
}
:root[data-theme="cute"]{
  --bg:
    radial-gradient(1200px 700px at 12% -8%, #ffe3f0 0%, rgba(255,227,240,0) 55%),
    radial-gradient(1100px 700px at 100% 0%, #ece0ff 0%, rgba(236,224,255,0) 55%),
    radial-gradient(900px 800px at 50% 120%, #ddfaee 0%, rgba(221,250,238,0) 60%),
    linear-gradient(160deg,#fdf3fb 0%, #f3eeff 50%, #eefaf5 100%);
  --surface:rgba(255,255,255,0.80); --surface-2:#fff6e6; --border:rgba(201,184,255,.55);
  --text:#5b5170; --text-dim:#8b81a3;
  --accent:#ff8fb8; --accent-2:#a893ff; --on-accent:#fff;
  --danger:#ff6b8a;
  --radius:34px; --radius-sm:22px;
  --font:"Hiragino Maru Gothic ProN","Hiragino Maru Gothic Pro","Yu Gothic",-apple-system,"Segoe UI",system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 18px 40px rgba(180,150,220,0.28);
  --glow:rgba(255,143,184,0.24);
  --wave-1:#ff8fb8; --wave-2:#a8ecd0;
}
:root[data-theme="clay"]{
  --bg:
    radial-gradient(1100px 700px at 12% -8%, #ffeef4, transparent 60%),
    radial-gradient(1000px 800px at 105% 10%, #e9fbf3, transparent 55%),
    radial-gradient(1200px 900px at 50% 120%, #f4ecff, transparent 60%),
    #eef0ff;
  --surface:linear-gradient(145deg,#fbfaff,#ecebfb); --surface-2:#ecebfb; --border:rgba(150,140,200,.16);
  --text:#4a4668; --text-dim:#7c78a0;
  --accent:#9e93f0; --accent-2:#6fd7a2; --on-accent:#fff;
  --danger:#ff7a7a;
  --radius:30px; --radius-sm:20px;
  --font:"Hiragino Maru Gothic ProN","Hiragino Sans","Yu Gothic",-apple-system,"Segoe UI Rounded","Segoe UI",system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:10px 12px 28px rgba(120,110,180,.30), -8px -8px 18px rgba(255,255,255,.9);
  --glow:rgba(158,147,240,0.22);
  --wave-1:#9e93f0; --wave-2:#6fd7a2;
}
:root[data-theme="mono"]{
  --bg:radial-gradient(80% 70% at 50% 40%, rgba(255,255,255,.05), transparent 70%), #0a0a0a;
  --surface:rgba(255,255,255,.045); --surface-2:rgba(255,255,255,.07); --border:rgba(244,244,242,.16);
  --text:#f4f4f2; --text-dim:#a7a7a3;
  --accent:#f4f4f2; --accent-2:#a7a7a3; --on-accent:#0a0a0a;
  --danger:#ff3b30;
  --radius:4px; --radius-sm:3px;
  --font:"Helvetica Neue",Helvetica,Arial,"Hiragino Kaku Gothic ProN","Noto Sans JP","Yu Gothic",Meiryo,system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 30px 70px -30px rgba(0,0,0,0.9);
  --glow:rgba(244,244,242,0.10);
  --wave-1:#f4f4f2; --wave-2:#a7a7a3;
}
:root[data-theme="neon"]{
  --bg:
    radial-gradient(1000px 700px at 80% -10%, rgba(37,230,255,0.10), transparent 60%),
    radial-gradient(900px 600px at 5% 110%, rgba(255,62,165,0.10), transparent 55%),
    #05070d;
  --surface:rgba(10,16,28,0.72); --surface-2:rgba(16,26,44,0.55); --border:rgba(80,160,240,0.18);
  --text:#dceaff; --text-dim:#7f93b3;
  --accent:#25e6ff; --accent-2:#ff3ea5; --on-accent:#05070d;
  --danger:#D83848;
  --radius:14px; --radius-sm:10px;
  --font:"Segoe UI",system-ui,-apple-system,"Helvetica Neue","Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif;
  --font-head:ui-monospace,"SFMono-Regular","Menlo","Consolas","Liberation Mono",monospace;
  --shadow:0 20px 60px -25px rgba(0,0,0,0.85);
  --glow:rgba(37,230,255,0.50);
  --wave-1:#25e6ff; --wave-2:#ff3ea5;
}
:root[data-theme="brutal"]{
  --bg:#f4f2ec;
  --surface:#ffffff; --surface-2:#ece9e0; --border:#111111;
  --text:#000000; --text-dim:#444444;
  --accent:#e5231b; --accent-2:#e5231b; --on-accent:#ffffff;
  --danger:#e5231b;
  --radius:0px; --radius-sm:0px;
  --font:"Helvetica Neue",Helvetica,Arial,"Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif;
  --font-head:"Arial Black","Arial Bold",Arial,"Hiragino Kaku Gothic ProN",sans-serif;
  --shadow:4px 4px 0 #000000;
  --glow:rgba(229,35,27,0.22);
  --wave-1:#000000; --wave-2:#e5231b;
}
:root[data-theme="wa"]{
  --bg:#f4efe4;
  --surface:#fbf7ee; --surface-2:#ece5d6; --border:#d9cfba;
  --text:#1a1714; --text-dim:rgba(26,23,20,0.55);
  --accent:#c8442e; --accent-2:#b08d4c; --on-accent:#fbf7ee;
  --danger:#a83320;
  --radius:6px; --radius-sm:3px;
  --font:"Hiragino Kaku Gothic ProN","Yu Gothic","Noto Sans JP",sans-serif;
  --font-head:"Hiragino Mincho ProN","Yu Mincho",YuMincho,"Noto Serif JP",serif;
  --shadow:0 20px 50px -28px rgba(26,23,20,0.4);
  --glow:rgba(200,68,46,0.22);
  --wave-1:#c8442e; --wave-2:#b08d4c;
}
:root[data-theme="crt"]{
  --bg:
    radial-gradient(1000px 700px at 50% 0%, rgba(255,176,0,0.06), transparent 60%),
    #0a0704;
  --surface:rgba(18,12,5,0.78); --surface-2:rgba(26,18,8,0.6); --border:rgba(255,176,0,0.22);
  --text:#ffb000; --text-dim:#c98600;
  --accent:#ffb000; --accent-2:#c98600; --on-accent:#0a0704;
  --danger:#ff6a3d;
  --radius:4px; --radius-sm:2px;
  --font:ui-monospace,"SFMono-Regular","Menlo","Consolas","Liberation Mono",monospace;
  --font-head:ui-monospace,"SFMono-Regular","Menlo","Consolas","Liberation Mono",monospace;
  --shadow:0 0 30px -6px rgba(255,176,0,0.18), 0 20px 50px -30px #000;
  --glow:rgba(255,176,0,0.50);
  --wave-1:#ffb000; --wave-2:#c98600;
}
:root[data-theme="water"]{
  --bg:
    radial-gradient(1100px 700px at 15% -8%, #d6f4ff 0%, transparent 55%),
    radial-gradient(1000px 700px at 100% 8%, #d9fbf1 0%, transparent 55%),
    linear-gradient(160deg,#f2fbff 0%, #e6f7fb 50%, #eafdf6 100%);
  --surface:rgba(255,255,255,0.72); --surface-2:#e3f4fa; --border:rgba(90,180,205,0.30);
  --text:#123a44; --text-dim:#5b8592;
  --accent:#12b5cc; --accent-2:#6fe3ef; --on-accent:#ffffff;
  --danger:#e0616b;
  --radius:22px; --radius-sm:15px;
  --font:-apple-system,BlinkMacSystemFont,"Segoe UI","Hiragino Kaku Gothic ProN","Hiragino Sans","Yu Gothic UI",Meiryo,system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 18px 40px -18px rgba(20,120,140,0.30);
  --glow:rgba(30,190,215,0.28);
  --wave-1:#12b5cc; --wave-2:#6fe3ef;
}
:root[data-theme="ocean"]{
  --bg:
    radial-gradient(1200px 800px at 78% -10%, rgba(20,90,120,0.45), transparent 60%),
    radial-gradient(1000px 700px at 8% 110%, rgba(10,60,80,0.40), transparent 55%),
    linear-gradient(160deg,#031820 0%, #052a34 45%, #04323b 100%);
  --surface:rgba(8,40,52,0.55); --surface-2:rgba(12,54,68,0.50); --border:rgba(90,200,210,0.20);
  --text:#dbf5f8; --text-dim:#84b3bd;
  --accent:#2dd4bf; --accent-2:#5fe6ff; --on-accent:#03202a;
  --danger:#e0616b;
  --radius:20px; --radius-sm:14px;
  --font:-apple-system,BlinkMacSystemFont,"Segoe UI","Hiragino Kaku Gothic ProN","Hiragino Sans","Yu Gothic UI",Meiryo,system-ui,sans-serif;
  --font-head:var(--font);
  --shadow:0 30px 80px -30px rgba(0,20,30,0.70);
  --glow:rgba(45,212,191,0.35);
  --wave-1:#2dd4bf; --wave-2:#5fe6ff;
}

/* Applied surfaces — only when a theme is active (user opted in). */
html[data-theme] body{ background:var(--bg) !important; background-attachment:fixed; color:var(--text); }
html[data-theme] .premeeting-screen{ background:var(--bg) !important; color:var(--text); }
html[data-theme] #layout_wrapper,
html[data-theme] #largeVideoContainer{ background:var(--bg) !important; }

/* 待機ルーム(プレジョイン): テーマ色に対応。中の文字は Jitsi 既定の固定色
   (明るいテーマだと白→白背景で不可視) なので、文字・入力欄・参加ボタンを
   テーマトークンで上書きして可読にする。 */
html[data-theme] .premeeting-screen,
html[data-theme] .premeeting-screen h1,
html[data-theme] .premeeting-screen h2,
html[data-theme] .premeeting-screen h3,
html[data-theme] .premeeting-screen p,
html[data-theme] .premeeting-screen label,
html[data-theme] .premeeting-screen span,
html[data-theme] .premeeting-screen a{ color:var(--text) !important; }
html[data-theme] .premeeting-screen svg{ fill:currentColor; }

/* 入力欄(名前など) */
html[data-theme] .premeeting-screen input{
  color:var(--text) !important;
  background:var(--surface-2) !important;
  border:1px solid var(--border) !important;
}
html[data-theme] .premeeting-screen input::placeholder{ color:var(--text-dim) !important; }

/* 参加ボタン(primary) はテーマの accent 塗り */
html[data-theme] .premeeting-screen .primary{
  background:var(--accent) !important; border:0 !important;
}
html[data-theme] .premeeting-screen .primary,
html[data-theme] .premeeting-screen .primary *{ color:var(--on-accent) !important; }
html[data-theme] .premeeting-screen .primary:hover{ filter:brightness(1.06); }

/* secondary ボタン */
html[data-theme] .premeeting-screen .secondary{
  background:var(--surface-2) !important; border:1px solid var(--border) !important;
}
html[data-theme] .premeeting-screen .secondary,
html[data-theme] .premeeting-screen .secondary *{ color:var(--text) !important; }

/* =====================================================================
   MENU BAR — demo (ai-app の .toolbar) と同一化
   枠(角丸長方形＋ブラー) / 各ボタン(縦積みタイル＋ラベル) / 状態色 / 字形
   ボタンごとの安定クラス .dai-tb-<key> は Toolbox.tsx で付与している。
   ===================================================================== */

/* .toolbar-wrap 相当: 画面下部に高さ --dai-bottom の「フッター帯」を確保し、
   その中にピルを中央寄せで固定する。浮きオーバーレイで他要素に被るのを防ぐ
   （ステージ/自分カメラ/会話履歴カラムはすべて bottom:var(--dai-bottom) で
   この帯の上端までで終わる = 重ならない）。 */
html[data-theme] .new-toolbox{
  background:transparent !important; border-top:0 !important;
  position:fixed !important; left:0 !important; right:0 !important; bottom:0 !important;
  top:auto !important;
  height:var(--dai-bottom) !important;
  display:flex !important; align-items:center !important; justify-content:center !important;
  padding:0 var(--dai-gap) !important;
  pointer-events:none;
  z-index:4;
}
/* 中間ラッパーの余白を消してピルを帯の中心に置く */
html[data-theme] .new-toolbox .toolbox-content,
html[data-theme] .new-toolbox .toolbox-content-wrapper{
  margin:0 !important; padding:0 !important; width:auto !important; max-width:100% !important;
  background:transparent !important;
}
html[data-theme] .new-toolbox .toolbox-content-wrapper::after{ display:none !important; }
html[data-theme] .new-toolbox .toolbox-content-items{ pointer-events:auto; }

/* 待機画面(プレジョイン)では上の「画面下フッター固定」を無効化し、マイク/映像ピルを
   参加ボタンの直下・参加カラム内に流し込む。右のパネル iframe に被らないようにするため。
   （.premeeting-screen で1段スコープを深くして上の !important に競り勝つ） */
html[data-theme] .premeeting-screen .new-toolbox{
  position:relative !important;
  left:auto !important; right:auto !important; top:auto !important; bottom:auto !important;
  height:auto !important;
  margin:10px auto 0 !important;
  padding:0 !important;
  z-index:auto !important;
}
html[data-theme] .premeeting-screen .new-toolbox .toolbox-content-items{
  margin:0 auto !important;
}

/* .toolbar 相当: 枠 */
html[data-theme] .toolbox-content-items{
  display:flex !important; align-items:flex-end;
  gap:clamp(6px, 1.4vw, 14px);
  padding:10px clamp(10px, 2vw, 18px) !important;
  border-radius:var(--radius) !important;
  background:var(--surface) !important;
  border:1px solid var(--border);
  box-shadow:var(--shadow);
  backdrop-filter:blur(18px) saturate(150%); -webkit-backdrop-filter:blur(18px) saturate(150%);
  max-width:100%; overflow-x:auto;
}
/* Jitsi 既定のボタン間 margin を無効化（gap を使う） */
html[data-theme] .toolbox-content-items > div{ margin:0 !important; }

/* .tbtn 相当: 各ボタンを縦積みに。ラベルは ::after で付与 */
html[data-theme] .toolbox-button{
  display:flex !important; flex-direction:column; align-items:center; gap:6px;
  line-height:1 !important; color:var(--text-dim) !important;
  font-family:var(--font); font-size:10.5px; font-weight:600;
}
/* ラベル（既定は aria-label、主要ボタンは名詞に上書き） */
html[data-theme] .toolbox-button::after{
  content:attr(aria-label); display:block; max-width:66px;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; text-align:center;
}
html[data-theme] .toolbox-button:has(.dai-tb-microphone)::after{ content:'マイク'; }
html[data-theme] .toolbox-button:has(.dai-tb-camera)::after{ content:'カメラ'; }
html[data-theme] .toolbox-button:has(.dai-tb-desktop)::after{ content:'画面共有'; }
html[data-theme] .toolbox-button:has(.dai-tb-closedcaptions)::after{ content:'字幕'; }
html[data-theme] .toolbox-button:has(.dai-tb-chat-history)::after{ content:'会話履歴'; }
html[data-theme] .toolbox-button:has(.dai-tb-slide-view)::after{ content:'スライド'; }

/* エコーキャンセル準備中 — 入室直後、エコー遅延が決まるまでの間だけ点灯する。
   確定したら消え、セッション中は再点灯しない。表示のみで音声経路には影響しない。 */
html[data-theme] .toolbox-button:has(.nkf-aec-preparing-badge){ position:relative; }
/* エコーキャンセルが動いていない（音声が処理されていない）— 準備中とは別物なので赤。 */
html[data-theme] .toolbox-button:has(.nkf-aec-settling-badge){ position:relative; }
/* 遅延は分かったが、フィルタ収束待ちでまだ送信していない。
   準備中(オレンジ)と区別する。自然に解消するので警告色にはしない。 */
html[data-theme] .nkf-aec-settling-badge{
  position:absolute; top:2px; right:8px; width:9px; height:9px;
  border-radius:50%; background:#3b82f6;
  box-shadow:0 0 0 0 rgba(59,130,246,.7);
  animation:dai-nkf-preparing 1.8s ease-out infinite; pointer-events:none; z-index:2;
}
html[data-theme] .toolbox-button:has(.nkf-aec-fault-badge){ position:relative; }
html[data-theme] .nkf-aec-fault-badge{
  position:absolute; top:2px; right:8px; width:9px; height:9px;
  border-radius:50%; background:#ef4444;
  box-shadow:0 0 0 0 rgba(239,68,68,.8);
  animation:dai-nkf-preparing 1.1s ease-out infinite; pointer-events:none; z-index:2;
}
html[data-theme] .nkf-aec-preparing-badge{
  position:absolute; top:2px; right:8px; width:9px; height:9px;
  border-radius:50%; background:var(--warning, #f5a524);
  box-shadow:0 0 0 0 rgba(245,165,36,.75);
  animation:dai-nkf-preparing 1.4s ease-out infinite; pointer-events:none; z-index:2;
}
@keyframes dai-nkf-preparing{
  0%{ box-shadow:0 0 0 0 rgba(245,165,36,.75); transform:scale(1); }
  70%{ box-shadow:0 0 0 7px rgba(245,165,36,0); transform:scale(1.1); }
  100%{ box-shadow:0 0 0 0 rgba(245,165,36,0); transform:scale(1); }
}

/* スライド新着アピール — スライドが出来たタイミングでボタンを強調してクリックを促す */
html[data-theme] .toolbox-button:has(.slide-attn-badge){ position:relative; }
html[data-theme] .toolbox-button:has(.slide-attn-badge) .toolbox-icon{
  animation:dai-slide-pop 1s ease-in-out infinite;
}
html[data-theme] .toolbox-button:has(.slide-attn-badge)::after{ color:var(--accent); }
html[data-theme] .slide-attn-badge{
  position:absolute; top:2px; right:8px; width:9px; height:9px;
  border-radius:50%; background:var(--accent, #4a90ff);
  box-shadow:0 0 0 0 rgba(74,144,255,.75);
  animation:dai-slide-badge 1.4s ease-out infinite; pointer-events:none; z-index:2;
}
@keyframes dai-slide-pop{
  0%,100%{ transform:translateY(0) scale(1); }
  50%{ transform:translateY(-3px) scale(1.16); }
}
@keyframes dai-slide-badge{
  0%{ box-shadow:0 0 0 0 rgba(74,144,255,.75); transform:scale(1); }
  70%{ box-shadow:0 0 0 9px rgba(74,144,255,0); transform:scale(1.2); }
  100%{ box-shadow:0 0 0 0 rgba(74,144,255,0); transform:scale(1); }
}
html[data-theme] .toolbox-button:has(.dai-tb-nkf-aec)::after{ content:'エコーキャンセル'; }
html[data-theme] .toolbox-button:has(.dai-tb-nkf-speaker)::after{ content:'スピーカー'; }
html[data-theme] .toolbox-button:has(.dai-tb-chat)::after{ content:'チャット'; }
html[data-theme] .toolbox-button:has(.dai-tb-raisehand)::after{ content:'挙手'; }
html[data-theme] .toolbox-button:has(.dai-tb-reactions)::after{ content:'リアクション'; }
html[data-theme] .toolbox-button:has(.dai-tb-noisesuppression)::after{ content:'ノイズ抑制'; }
html[data-theme] .toolbox-button:has(.dai-tb-participants-pane)::after{ content:'参加者'; }
html[data-theme] .toolbox-button:has(.dai-tb-tileview)::after{ content:'映像レイアウト'; white-space:nowrap; max-width:none; }
html[data-theme] .toolbox-button:has(.dai-tb-invite)::after{ content:'招待'; }
html[data-theme] .toolbox-button:has(.dai-tb-security)::after{ content:'セキュリティ'; }
html[data-theme] .toolbox-button:has(.dai-tb-settings)::after{ content:'設定'; }
html[data-theme] .toolbox-button:has(.dai-tb-profile)::after{ content:'プロフィール'; }

/* .tbtn .ic 相当: アイコンタイル */
html[data-theme] .toolbox-icon{
  width:50px !important; height:50px !important;
  border-radius:16px !important;
  display:grid !important; place-items:center;
  background:var(--surface-2) !important;
  border:1px solid var(--border) !important;
  color:var(--text);
  transition:transform .18s, background .18s, border-color .18s, box-shadow .18s, color .18s;
}
html[data-theme] .toolbox-icon svg{ width:22px !important; height:22px !important; }
/* Jitsi 標準アイコンは固定色（明るいテーマで白→不可視）になりがち。
   currentColor に矯正してテーマのアイコン色に追従させる。 */
html[data-theme] .toolbox-icon svg,
html[data-theme] .toolbox-icon svg path,
html[data-theme] .toolbox-icon svg rect,
html[data-theme] .toolbox-icon svg circle,
html[data-theme] .toolbox-icon svg polygon{ fill:currentColor !important; }
html[data-theme] .toolbox-button:hover .toolbox-icon{ transform:translateY(-2px); }

/* 状態: 既定の toggled = ON=accent（履歴/字幕/画面共有/挙手 等） */
html[data-theme] .toolbox-icon.toggled{
  background:var(--accent) !important; border-color:transparent !important;
  color:var(--on-accent) !important; box-shadow:0 8px 22px -6px var(--glow);
}
html[data-theme] .toolbox-button:has(.toolbox-icon.toggled)::after{ color:var(--accent); }

/* マイク/カメラ: 非ミュート=ON → accent */
html[data-theme] .toolbox-icon.dai-tb-microphone:not(.toggled),
html[data-theme] .toolbox-icon.dai-tb-camera:not(.toggled){
  background:var(--accent) !important; border-color:transparent !important;
  color:var(--on-accent) !important; box-shadow:0 8px 22px -6px var(--glow);
}
/* マイク/カメラ: toggled=ミュート → OFF(赤枠) に反転（generic より後に置き優先） */
html[data-theme] .toolbox-icon.dai-tb-microphone.toggled,
html[data-theme] .toolbox-icon.dai-tb-camera.toggled{
  background:var(--surface-2) !important; border-color:var(--danger) !important;
  color:var(--danger) !important; box-shadow:none !important;
}
html[data-theme] .toolbox-button:has(.dai-tb-microphone.toggled)::after,
html[data-theme] .toolbox-button:has(.dai-tb-camera.toggled)::after{ color:var(--danger); }

/* 退出: 常に danger 塗り（通常時は .hangup-button、会議終了メニュー有効時は
   .hangup-menu-button になるため両方を対象にする） */
html[data-theme] .toolbox-icon.hangup-button,
html[data-theme] .toolbox-icon.hangup-menu-button{
  background:var(--danger) !important; border-color:transparent !important; color:#fff !important;
}
html[data-theme] .toolbox-button:has(.hangup-button)::after,
html[data-theme] .toolbox-button:has(.hangup-menu-button)::after{ content:'退出'; color:var(--danger); }

/* アイコン字形は各ボタンの実SVG（Jitsi標準）を使用し、上の fill:currentColor で
   テーマ色に追従させる。AEC(エコーキャンセル)だけは波形の実SVGアイコン
   (nkf-aec/components/web/IconNkfAecWave) をボタン側で指定している。
   ※以前の ::before + CSS mask 差し替えは描画されず空タイルになったため撤去。 */

/* =====================================================================
   THEMED CONFERENCE LAYOUT — demo composition (合意済みの図)
   上部バー / AIカメラ(主映像) / 自分カメラ(右上小窓) / 会話履歴(右カラム) / メニュー
   ===================================================================== */
html[data-theme]{ --dai-top:56px; --dai-col:340px; --dai-gap:16px; --dai-bottom:116px; --dai-wave:96px; }
html[data-theme] #videospace{ background:transparent !important; }

/* 会議画面上部の既定情報バーは DeepaiTopBar と重複するため隠す。 */
html[data-theme] .subject-info-container{ display:none !important; }

/* AIカメラ = 主映像。左ステージを占有し、レターボックスのグレー余白を除去。 */
html[data-theme] #largeVideoContainer{
  position:fixed !important;
  left:var(--dai-gap);
  top:calc(var(--dai-top) + var(--dai-gap));
  right:calc(var(--dai-col) + var(--dai-gap) * 2);
  bottom:var(--dai-bottom);
  width:auto !important; height:auto !important;
  border-radius:var(--radius); overflow:hidden;
  box-shadow:inset 0 0 0 1px var(--border), 0 24px 70px -30px var(--glow);
  z-index:1;
}

/* AI発話インジケータ = AIバッジ。今の波形(VoiceWaveOverlay)をそのまま小さく、
   AI映像の左下に置いた小さなバッジ内に収める(全幅の帯は廃止＝映像を隠さない)。 */
html[data-theme] .deepai-ai-wave{
  position:fixed !important;
  left:calc(var(--dai-gap) + 12px);
  bottom:calc(var(--dai-bottom) + 12px);
  height:26px;
  display:flex !important; align-items:center; gap:7px;
  padding:0 11px 0 8px;
  border-radius:999px;
  background:rgba(0,0,0,0.42);
  backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
  border:1px solid var(--border);
  z-index:3;
  pointer-events:none;
}
/* 波形の器（VoiceWaveOverlay は inset:0 でこの中に満ちる） */
html[data-theme] .deepai-ai-wave .dai-wave-box{
  position:relative; width:42px; height:16px; border-radius:4px; overflow:hidden; flex:0 0 auto;
}
html[data-theme] .deepai-ai-wave .dai-wave-ai{
  font-family:var(--font); font-weight:700; font-size:10px; letter-spacing:.06em; color:var(--accent);
}
/* 「AIの発話を止める」ボタン：下中央のフローティング丸四角アイコンボタン
   （モバイルでは左メニュー・右終了のFABの間）。常時表示・常時クリック可。 */
html[data-theme] .deepai-stop-ai{
  position:fixed !important;
  left:50%; transform:translateX(-50%);
  bottom:calc(var(--dai-bottom) + 52px);
  z-index:350;
  width:52px; height:52px; padding:0;
  display:grid; place-items:center;
  border-radius:16px;
  color:#fff; cursor:pointer;
  background:linear-gradient(135deg,#e53935,#b71c1c);
  border:1px solid rgba(255,255,255,0.28);
  box-shadow:0 8px 24px rgba(229,57,53,0.45);
  backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
  transition:transform .16s ease, box-shadow .2s ease;
}
html[data-theme] .deepai-stop-ai:active{ transform:translateX(-50%) scale(.94); }
html[data-theme] .deepai-stop-ai svg{ width:24px; height:24px; }
html[data-theme] #largeVideoElementsContainer,
html[data-theme] #largeVideoWrapper,
html[data-theme] #largeVideo{
  position:absolute !important; inset:0 !important;
  width:100% !important; height:100% !important; margin:0 !important;
}
html[data-theme] #largeVideoBackgroundContainer{ display:none !important; }
html[data-theme] #largeVideo video{ width:100% !important; height:100% !important; object-fit:cover !important; }

/* 自分のカメラ = ステージ右上の小窓。 */
html[data-theme] #filmstripLocalVideo{
  position:fixed !important;
  top:calc(var(--dai-top) + var(--dai-gap) + 8px) !important;
  right:calc(var(--dai-col) + var(--dai-gap) * 2 + 12px) !important;
  width:188px !important; height:118px !important;
  z-index:6 !important;
  pointer-events:auto !important;
  border-radius:var(--radius-sm) !important; overflow:hidden;
  box-shadow:var(--shadow); border:1px solid var(--border);
}
html[data-theme] #filmstripLocalVideo #filmstripLocalVideoThumbnail,
html[data-theme] #filmstripLocalVideo #localVideoContainer{
  width:188px !important; height:118px !important; min-width:0 !important;
}
html[data-theme] #filmstripLocalVideo video{ object-fit:cover !important; }

/* 参加者レイアウト = 「表示なし(none)」のときだけ他参加者を隠す(既定)。
   親のフィルムストリップ容器は透明のまま残ってクリックを吸ってしまうので、
   pointer-events を無効化する(自分の小窓 #filmstripLocalVideo だけ上で auto に戻す)。
   none 以外を選んだときは Jitsi 標準のフィルムストリップで参加者が表示される
   (各レイアウトの専用配置は順次追加)。 */
html[data-theme][data-participants-layout="none"] .remote-videos{ display:none !important; }
html[data-theme][data-participants-layout="none"] .filmstrip,
html[data-theme][data-participants-layout="none"] #remoteVideos{
  background:transparent !important;
  pointer-events:none !important;
}

/* 会話履歴 = 右カラム常設(テーマ色)。 */
html[data-theme] .chat-history-panel{
  position:fixed !important;
  right:var(--dai-gap) !important;
  top:calc(var(--dai-top) + var(--dai-gap)) !important;
  bottom:var(--dai-bottom) !important;
  width:var(--dai-col) !important; height:auto !important;
  transform:none !important;
  border-radius:var(--radius); overflow:hidden;
  background:var(--surface) !important; color:var(--text);
  border:1px solid var(--border); box-shadow:var(--shadow);
  z-index:3;
}
html[data-theme] .chat-history-title,
html[data-theme] .chat-history-panel h2{ color:var(--text) !important; }

/* 話者(dominant)タイルにテーマ色のリングだけ。以前の下端マスクSVG波形は
   VoiceWaveOverlay(canvas波形)に置き換えたため撤去。 */
html[data-theme] .dominant-speaker{
  box-shadow:0 0 0 2px var(--accent), 0 0 42px -8px var(--glow) !important;
}

/* =====================================================================
   AI STAGE DECO — テーマ別のサイド装飾。AIがステージ(data-deepai-ai="1")の
   ときだけ、映像を中央の正方形にインセットし、左右の余白を装飾で埋める。
   ===================================================================== */
html[data-theme] #largeVideoContainer[data-deepai-ai="1"] #largeVideoElementsContainer{ inset:0 !important; }
html[data-theme] #largeVideoContainer[data-deepai-ai="1"] #largeVideoWrapper{
  position:absolute !important; inset:auto !important;
  top:50% !important; left:50% !important;
  height:86% !important; width:auto !important; aspect-ratio:1/1;
  transform:translate(-50%,-50%) !important;
  border-radius:var(--radius-sm) !important; overflow:hidden;
  border:2px solid var(--border); box-shadow:0 24px 60px -18px rgba(0,0,0,.5);
  z-index:2;
}
html[data-theme] #largeVideoContainer[data-deepai-ai="1"] #largeVideo,
html[data-theme] #largeVideoContainer[data-deepai-ai="1"] #largeVideo video{
  position:absolute !important; inset:0 !important;
  width:100% !important; height:100% !important; object-fit:cover !important; transform:none !important;
}

/* 装飾レイヤー（映像の背面 z:0） */
.deepai-stage-deco{ position:absolute; inset:0; overflow:hidden; pointer-events:none; z-index:0; }

@keyframes dai-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-24px)}}
@keyframes dai-bob{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(4deg)}}
@keyframes dai-spin{to{transform:rotate(360deg)}}
@keyframes dai-scan{0%{transform:translateY(-100%)}100%{transform:translateY(1100%)}}
@keyframes dai-flick{0%,97%{opacity:1}98%{opacity:.75}100%{opacity:1}}
@keyframes dai-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
@keyframes dai-breatheY{0%,100%{transform:translateY(-50%) scale(1)}50%{transform:translateY(-50%) scale(1.04)}}
@keyframes dai-drift{0%,100%{transform:translate(0,0)}50%{transform:translate(0,-7px)}}
@keyframes dai-glowp{0%,100%{opacity:.6}50%{opacity:1}}
@keyframes dai-fadey{0%,100%{opacity:.5}50%{opacity:.9}}
@keyframes dai-waft{from{background-position:0 0,28px 14px}to{background-position:56px 0,84px 14px}}
@keyframes dai-rise{0%{transform:translateY(30px);opacity:0}20%{opacity:.75}100%{transform:translateY(-140px);opacity:0}}

/* 共通パーツ */
.deepai-stage-deco .dai-orb{position:absolute;border-radius:50%;filter:blur(44px);mix-blend-mode:screen;animation:dai-float 9s ease-in-out infinite}
.deepai-stage-deco .dai-float{position:absolute;animation:dai-bob 6s ease-in-out infinite;font-size:26px}

/* default（控えめ青オーブ）*/
html[data-theme=""] .deepai-stage-deco{}
.deepai-stage-deco .dai-orb.a{left:-70px;top:22%;width:280px;height:280px;background:var(--accent);opacity:.55}
.deepai-stage-deco .dai-orb.b{right:-70px;top:40%;width:300px;height:300px;background:#7c5cff;opacity:.5;animation-delay:-4s}

/* COOL（目立つ）*/
html[data-theme="cool"] .deepai-stage-deco .dai-orb{opacity:.85}
html[data-theme="cool"] .deepai-stage-deco .dai-orb.a{left:-60px;top:14%;width:360px;height:360px;background:var(--accent)}
html[data-theme="cool"] .deepai-stage-deco .dai-orb.b{right:-70px;top:42%;width:400px;height:400px;background:#7c5cff}
html[data-theme="cool"] .deepai-stage-deco .dai-orb.c{left:30%;bottom:-120px;width:280px;height:280px;background:var(--accent-2);opacity:.6;animation-delay:-6s}
html[data-theme="cool"] .deepai-stage-deco::after{content:"";position:absolute;inset:0;background:repeating-linear-gradient(115deg,transparent 0 54px,rgba(120,190,255,.09) 54px 56px)}

/* MINIMAL */
html[data-theme="minimal"] .deepai-stage-deco .dai-mring{position:absolute;left:-90px;top:50%;transform:translateY(-50%);width:380px;height:380px;border-radius:50%;border:1.5px solid var(--accent);opacity:.16;animation:dai-fadey 9s ease-in-out infinite}
html[data-theme="minimal"] .deepai-stage-deco .dai-msq{position:absolute;right:12%;top:26%;width:40px;height:40px;background:var(--accent);border-radius:8px;box-shadow:0 10px 24px -8px rgba(36,111,229,.5);animation:dai-breathe 6s ease-in-out infinite}
html[data-theme="minimal"] .deepai-stage-deco .dai-marc{position:absolute;right:8%;bottom:16%;width:120px;height:120px;border-radius:50%;border:6px solid var(--accent);border-color:var(--accent) transparent transparent transparent;opacity:.5;animation:dai-spin 24s linear infinite}
html[data-theme="minimal"] .deepai-stage-deco .dai-hair{position:absolute;top:16%;bottom:16%;width:2px;background:var(--accent);opacity:.5;animation:dai-fadey 5s ease-in-out infinite}
html[data-theme="minimal"] .deepai-stage-deco .dai-hair.l{left:12%}

/* LUX */
html[data-theme="lux"] .deepai-stage-deco .dai-frameline{position:absolute;inset:22px;border:1px solid rgba(200,168,96,.35);animation:dai-glowp 6s ease-in-out infinite}
html[data-theme="lux"] .deepai-stage-deco .dai-mono{position:absolute;left:8%;top:50%;transform:translateY(-50%);font-family:Georgia,serif;font-size:60px;color:rgba(200,168,96,.35);letter-spacing:2px;animation:dai-fadey 7s ease-in-out infinite}
html[data-theme="lux"] .deepai-stage-deco .dai-dust{position:absolute;width:4px;height:4px;border-radius:50%;background:var(--accent-2);box-shadow:0 0 8px var(--accent);opacity:.7;animation:dai-float 10s ease-in-out infinite}
html[data-theme="lux"] .deepai-stage-deco .dai-dust.d1{left:12%;top:30%}
html[data-theme="lux"] .deepai-stage-deco .dai-dust.d2{left:16%;top:64%;animation-delay:-3s}
html[data-theme="lux"] .deepai-stage-deco .dai-dust.d3{right:14%;top:40%;animation-delay:-5s}

/* NATURAL */
html[data-theme="natural"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:radial-gradient(300px 300px at 12% 20%,rgba(138,154,111,.18),transparent 70%),radial-gradient(280px 280px at 90% 80%,rgba(201,138,104,.16),transparent 70%);animation:dai-breathe 12s ease-in-out infinite}
html[data-theme="natural"] .deepai-stage-deco .dai-float{opacity:.7;animation-duration:9s}
html[data-theme="natural"] .deepai-stage-deco .dai-float.f1{left:9%;top:24%}
html[data-theme="natural"] .deepai-stage-deco .dai-float.f2{left:14%;top:62%;animation-delay:-2s}
html[data-theme="natural"] .deepai-stage-deco .dai-float.f3{right:11%;top:30%;animation-delay:-4s}

/* POP */
html[data-theme="pop"] .deepai-stage-deco .dai-dotp{position:absolute;border-radius:50%;animation:dai-bob 5s ease-in-out infinite}
html[data-theme="pop"] .deepai-stage-deco .dai-dotp.p1{left:8%;top:20%;width:70px;height:70px;background:#8B5CF6}
html[data-theme="pop"] .deepai-stage-deco .dai-dotp.p2{left:14%;bottom:16%;width:44px;height:44px;background:#2FD3A5;animation-delay:-1s}
html[data-theme="pop"] .deepai-stage-deco .dai-dotp.p3{right:9%;top:26%;width:60px;height:60px;background:#FF6B6B;animation-delay:-2s}
html[data-theme="pop"] .deepai-stage-deco .dai-dotp.p4{right:14%;bottom:20%;width:36px;height:36px;background:#5fe6ff;animation-delay:-1.5s}

/* CUTE */
html[data-theme="cute"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:radial-gradient(300px 300px at 14% 26%,rgba(255,143,184,.18),transparent 70%),radial-gradient(320px 320px at 88% 74%,rgba(168,147,255,.16),transparent 70%)}
html[data-theme="cute"] .deepai-stage-deco .dai-blob{position:absolute;border-radius:50%;background:rgba(255,255,255,.4);animation:dai-breathe 7s ease-in-out infinite}
html[data-theme="cute"] .deepai-stage-deco .dai-blob.b1{left:7%;top:20%;width:110px;height:110px}
html[data-theme="cute"] .deepai-stage-deco .dai-blob.b2{right:8%;bottom:16%;width:80px;height:80px;animation-delay:-3s}
html[data-theme="cute"] .deepai-stage-deco .dai-float{font-size:24px;opacity:.7;animation-duration:8s}
html[data-theme="cute"] .deepai-stage-deco .dai-float.f1{left:11%;top:32%}
html[data-theme="cute"] .deepai-stage-deco .dai-float.f2{right:12%;top:26%;animation-delay:-3s}

/* CLAY */
html[data-theme="clay"] .deepai-stage-deco .dai-puff{position:absolute;border-radius:44% 56% 52% 48%/52% 46% 54% 48%;box-shadow:10px 12px 24px rgba(120,110,180,.28),-8px -8px 16px rgba(255,255,255,.9);animation:dai-float 9s ease-in-out infinite}
html[data-theme="clay"] .deepai-stage-deco .dai-puff.p1{left:8%;top:22%;width:120px;height:120px;background:#c9c2f5}
html[data-theme="clay"] .deepai-stage-deco .dai-puff.p2{right:9%;bottom:18%;width:96px;height:96px;background:#bfead4;animation-delay:-3s}

/* MONO */
html[data-theme="mono"] .deepai-stage-deco .dai-ring{position:absolute;border:1px solid rgba(244,244,242,.28);border-radius:50%;animation:dai-spin 30s linear infinite}
html[data-theme="mono"] .deepai-stage-deco .dai-ring.r1{left:-50px;top:6%;width:300px;height:300px}
html[data-theme="mono"] .deepai-stage-deco .dai-ring.r2{right:-70px;bottom:-60px;width:360px;height:360px;animation-direction:reverse}
html[data-theme="mono"] .deepai-stage-deco .dai-mono-big{position:absolute;left:3%;top:50%;transform:translateY(-50%);font:800 140px/.8 "Helvetica Neue",Arial,sans-serif;letter-spacing:-6px;color:transparent;-webkit-text-stroke:1.5px rgba(244,244,242,.4);animation:dai-fadey 7s ease-in-out infinite}
html[data-theme="mono"] .deepai-stage-deco .dai-tick{position:absolute;right:7%;top:calc(50% - 65px);width:130px;height:130px;border-radius:50%;background:repeating-conic-gradient(rgba(244,244,242,.55) 0 1.6deg,transparent 1.6deg 30deg);-webkit-mask:radial-gradient(transparent 45%,#000 46%);mask:radial-gradient(transparent 45%,#000 46%);animation:dai-spin 22s linear infinite}
html[data-theme="mono"] .deepai-stage-deco .dai-vline{position:absolute;top:0;bottom:0;left:15%;width:1px;background:rgba(244,244,242,.5);animation:dai-float 6s ease-in-out infinite}

/* NEON */
html[data-theme="neon"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:linear-gradient(rgba(37,230,255,.10) 1px,transparent 1px) 0 0/100% 32px,linear-gradient(90deg,rgba(37,230,255,.10) 1px,transparent 1px) 0 0/32px 100%;-webkit-mask:linear-gradient(90deg,#000 0 22%,transparent 40% 60%,#000 78% 100%);mask:linear-gradient(90deg,#000 0 22%,transparent 40% 60%,#000 78% 100%)}
html[data-theme="neon"] .deepai-stage-deco .dai-scanline{position:absolute;left:0;right:0;height:80px;background:linear-gradient(rgba(37,230,255,0),rgba(37,230,255,.14),rgba(37,230,255,0));animation:dai-scan 4s linear infinite}
html[data-theme="neon"] .deepai-stage-deco .dai-brk{position:absolute;width:54px;height:54px;border:2px solid var(--accent);box-shadow:0 0 14px var(--accent);animation:dai-glowp 3s ease-in-out infinite}
html[data-theme="neon"] .deepai-stage-deco .dai-brk.tl{left:14px;top:14px;border-right:0;border-bottom:0}
html[data-theme="neon"] .deepai-stage-deco .dai-brk.tr{right:14px;top:14px;border-left:0;border-bottom:0;border-color:var(--accent-2);box-shadow:0 0 14px var(--accent-2)}
html[data-theme="neon"] .deepai-stage-deco .dai-brk.bl{left:14px;bottom:14px;border-right:0;border-top:0;border-color:var(--accent-2);box-shadow:0 0 14px var(--accent-2)}
html[data-theme="neon"] .deepai-stage-deco .dai-brk.br{right:14px;bottom:14px;border-left:0;border-top:0}
html[data-theme="neon"] .deepai-stage-deco .dai-dl{position:absolute;height:2px;background:linear-gradient(90deg,transparent,var(--accent),transparent);opacity:.7;animation:dai-fadey 2.5s ease-in-out infinite}
html[data-theme="neon"] .deepai-stage-deco .dai-dl.d1{left:6%;top:34%;width:16%}
html[data-theme="neon"] .deepai-stage-deco .dai-dl.d2{left:6%;top:40%;width:11%}
html[data-theme="neon"] .deepai-stage-deco .dai-dl.d3{right:6%;bottom:34%;width:16%}

/* BRUTAL */
html[data-theme="brutal"] .deepai-stage-deco .dai-rule{position:absolute;left:0;right:0;height:6px;background:#000}
html[data-theme="brutal"] .deepai-stage-deco .dai-rule.top{top:22%}
html[data-theme="brutal"] .deepai-stage-deco .dai-rule.bot{bottom:22%}
html[data-theme="brutal"] .deepai-stage-deco .dai-block{position:absolute;left:8%;top:32%;width:70px;height:70px;background:var(--accent);animation:dai-drift 6s ease-in-out infinite}
html[data-theme="brutal"] .deepai-stage-deco .dai-outline{position:absolute;right:8%;bottom:28%;width:90px;height:90px;border:6px solid #000;animation:dai-drift 7s ease-in-out infinite;animation-delay:-2s}

/* 和 */
html[data-theme="wa"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;--c:rgba(176,141,76,.5);background-image:radial-gradient(circle at 50% 100%,transparent 33%,var(--c) 33%,var(--c) 41%,transparent 41%,transparent 66%,var(--c) 66%,var(--c) 74%,transparent 74%),radial-gradient(circle at 50% 100%,transparent 33%,var(--c) 33%,var(--c) 41%,transparent 41%,transparent 66%,var(--c) 66%,var(--c) 74%,transparent 74%);background-size:56px 28px;background-position:0 0,28px 14px;opacity:.55;-webkit-mask:linear-gradient(90deg,#000 0 24%,transparent 42% 58%,#000 76% 100%);mask:linear-gradient(90deg,#000 0 24%,transparent 42% 58%,#000 76% 100%);animation:dai-waft 26s linear infinite}
html[data-theme="wa"] .deepai-stage-deco .dai-torii{position:absolute;left:3%;bottom:0;height:74%;width:auto;opacity:.5;filter:drop-shadow(0 6px 10px rgba(120,40,20,.15));animation:dai-fadey 10s ease-in-out infinite}
html[data-theme="wa"] .deepai-stage-deco .dai-hinomaru{position:absolute;right:9%;top:12%;width:96px;height:96px;border-radius:50%;background:radial-gradient(circle at 40% 38%,#d8543e,#b5301f);box-shadow:0 0 34px rgba(200,68,46,.35);opacity:.85;animation:dai-breathe 9s ease-in-out infinite}
html[data-theme="wa"] .deepai-stage-deco .dai-sen{position:absolute;right:8%;top:10%;bottom:10%;width:5px;background:var(--accent);animation:dai-fadey 6s ease-in-out infinite}
html[data-theme="wa"] .deepai-stage-deco .dai-kanji{position:absolute;right:calc(8% + 16px);top:14%;writing-mode:vertical-rl;font-family:"Hiragino Mincho ProN",serif;font-size:22px;color:rgba(26,23,20,.5);letter-spacing:4px;animation:dai-fadey 8s ease-in-out infinite}

/* CRT */
html[data-theme="crt"] .deepai-stage-deco{animation:dai-flick 6s infinite}
html[data-theme="crt"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:repeating-linear-gradient(0deg,rgba(255,176,0,.06) 0 2px,transparent 2px 4px)}
html[data-theme="crt"] .deepai-stage-deco .dai-term{position:absolute;font-family:ui-monospace,monospace;font-size:12px;color:rgba(255,176,0,.75);white-space:pre;text-shadow:0 0 6px rgba(255,176,0,.6);animation:dai-fadey 4s ease-in-out infinite}
html[data-theme="crt"] .deepai-stage-deco .dai-term.t1{left:6%;top:24%}
html[data-theme="crt"] .deepai-stage-deco .dai-term.t2{right:6%;bottom:26%;text-align:right}

/* WATER（明るいアクア: 泡＋波紋）*/
html[data-theme="water"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:radial-gradient(320px 320px at 12% 22%,rgba(18,181,204,.16),transparent 70%),radial-gradient(320px 320px at 90% 78%,rgba(111,227,239,.18),transparent 70%)}
html[data-theme="water"] .deepai-stage-deco .dai-bub{position:absolute;bottom:0;border-radius:50%;background:radial-gradient(circle at 35% 30%,rgba(255,255,255,.9),rgba(111,227,239,.35));box-shadow:0 0 10px rgba(18,181,204,.3);animation:dai-rise 7s ease-in infinite}
html[data-theme="water"] .deepai-stage-deco .dai-bub.w1{left:10%;width:18px;height:18px}
html[data-theme="water"] .deepai-stage-deco .dai-bub.w2{left:16%;width:12px;height:12px;animation-delay:-2.5s}
html[data-theme="water"] .deepai-stage-deco .dai-bub.w3{right:12%;width:22px;height:22px;animation-delay:-4.5s}
html[data-theme="water"] .deepai-stage-deco .dai-ripple{position:absolute;left:8%;top:50%;width:120px;height:120px;border-radius:50%;border:2px solid rgba(18,181,204,.4);animation:dai-breathe 6s ease-in-out infinite;opacity:.5}

/* OCEAN（深海: 光条＋泡）*/
html[data-theme="ocean"] .deepai-stage-deco::before{content:"";position:absolute;inset:0;background:radial-gradient(420px 300px at 50% -20%,rgba(95,230,255,.14),transparent 70%)}
html[data-theme="ocean"] .deepai-stage-deco .dai-ray{position:absolute;inset:0;background:repeating-linear-gradient(105deg,transparent 0 70px,rgba(95,230,255,.06) 70px 74px);-webkit-mask:linear-gradient(#000,transparent 70%);mask:linear-gradient(#000,transparent 70%);animation:dai-fadey 8s ease-in-out infinite}
html[data-theme="ocean"] .deepai-stage-deco .dai-bub{position:absolute;bottom:0;border-radius:50%;background:radial-gradient(circle at 35% 30%,rgba(255,255,255,.7),rgba(45,212,191,.3));box-shadow:0 0 10px rgba(45,212,191,.35);animation:dai-rise 8s ease-in infinite}
html[data-theme="ocean"] .deepai-stage-deco .dai-bub.w1{left:11%;width:16px;height:16px}
html[data-theme="ocean"] .deepai-stage-deco .dai-bub.w2{left:17%;width:10px;height:10px;animation-delay:-3s}
html[data-theme="ocean"] .deepai-stage-deco .dai-bub.w3{right:13%;width:20px;height:20px;animation-delay:-5s}

/* =====================================================================
   MOBILE — 幅狭(縦スマホ) or 高さ低い(横スマホ) で発動。
   AIカメラ全幅 / 自分カメラ小 / ツールバーはFAB(☰)で展開 / 会話履歴は
   縦=下部シート・横=右サイド。FABは DeepaiMobileToolbar が描画。
   ===================================================================== */
@media (max-width: 700px), (max-height: 540px){
  html[data-theme]{ --dai-top:48px; --dai-col:0px; --dai-gap:10px; --dai-bottom:88px; }

  /* 上部バーをコンパクトに */
  .deepai-topbar{ height:48px !important; gap:8px !important; padding:0 12px !important; }
  .deepai-topbar .status{ display:none !important; }
  .deepai-topbar .brand{ font-size:13px !important; }
  .deepai-topbar .theme-trigger{ font-size:11.5px !important; padding:6px 10px !important; }

  /* AIカメラ = 全幅(右カラム予約なし) */
  html[data-theme] #largeVideoContainer{
    left:var(--dai-gap) !important; right:var(--dai-gap) !important;
    top:calc(var(--dai-top) + var(--dai-gap)) !important;
    bottom:var(--dai-bottom) !important;
  }

  /* 自分カメラ 小 */
  html[data-theme] #filmstripLocalVideo{
    width:92px !important; height:58px !important;
    top:calc(var(--dai-top) + var(--dai-gap) + 4px) !important;
    right:calc(var(--dai-gap) + 4px) !important;
  }
  html[data-theme] #filmstripLocalVideo #filmstripLocalVideoThumbnail,
  html[data-theme] #filmstripLocalVideo #localVideoContainer{ width:92px !important; height:58px !important; }

  /* 停止ボタン: FAB帯(bottom:16px, 高さ56px)の中央 = 左メニューと右終了の間。
     会話履歴(z-index 2/300)より前面に出す。 */
  html[data-theme] .deepai-stop-ai{
    top:auto !important;
    bottom:18px !important;
    z-index:411 !important;
  }

  /* ツールバー: 既定は隠し、FAB(☰)で data-dai-toolbar=open にして展開。
     Jitsi基本CSSは .toolbox-content が position:relative;z-index:250 で入れ子が
     複雑なため、ピル(.toolbox-content-items)自体を画面下中央に fixed 配置して
     「中央」かつ「最前面(FABの下)」を確実にする。 */
  html[data-theme] .new-toolbox{ z-index:409 !important; pointer-events:none !important; background:transparent !important; }
  html[data-theme] .new-toolbox .toolbox-content,
  html[data-theme] .new-toolbox .toolbox-content-wrapper{ margin:0 !important; padding:0 !important; width:auto !important; }

  html[data-theme] .toolbox-content-items{
    position:fixed !important; left:50% !important; bottom:84px !important;
    transform:translateX(-50%) translateY(180%);
    z-index:409 !important; margin:0 !important;
    display:flex !important; flex-wrap:wrap !important; justify-content:center !important;
    align-items:center !important; gap:10px !important;
    max-width:calc(100vw - 24px) !important; overflow:visible !important;
    opacity:0; pointer-events:none;
    transition:transform .22s ease, opacity .22s ease;
  }
  html[data-theme][data-dai-toolbar="open"] .toolbox-content-items{
    transform:translateX(-50%) translateY(0);
    opacity:1; pointer-events:auto;
  }

  /* メニュー展開中は会話履歴を隠す。z-index の入れ子文脈でメニューが会話履歴の
     後ろに潜る問題を、そもそも重なる相手を消すことで確実に解消する。 */
  html[data-theme][data-dai-toolbar="open"] .chat-history-panel{
    display:none !important;
  }
  /* メニューの入れ子ラッパーが作る重なり文脈のトラップを無効化し、
     ピルの z-index:409 を確実に最上位で効かせる(保険) */
  html[data-theme] .new-toolbox .toolbox-content,
  html[data-theme] .new-toolbox .toolbox-content-wrapper{ z-index:auto !important; }
  /* メニュー展開中は背後を少し暗くして、ボタンを見やすく */
  html[data-theme][data-dai-toolbar="open"] #largeVideoContainer{ filter:brightness(.6); }

  /* 退出は右下FABにあるので、展開メニュー内の退出ボタンは隠す(重複) */
  html[data-theme] .toolbox-content-items .toolbox-button:has(.hangup-button),
  html[data-theme] .toolbox-content-items .toolbox-button:has(.hangup-menu-button),
  html[data-theme] .toolbox-content-items .toolbox-button-wth-dialog:has(.hangup-menu-button){
    display:none !important;
  }

  /* スマホの会話履歴ヘッダー = 1行に集約:
       [会話履歴]  (すべて|ユーザー|AI)  [📊 会話を評価]
     .chat-history-topbar を flex 行にし、.chat-history-header は display:contents で
     溶かして子(タイトル/ボタン)を同じ行の flex アイテムに昇格させる。
     DOM順は タイトル→ボタン→フィルタ なので order で並べ替える。 */
  html[data-theme] .chat-history-panel .chat-history-topbar{
    display:flex !important; align-items:center !important; gap:8px !important;
    padding:10px 12px !important;
    border-bottom:1px solid var(--border, rgba(140,165,230,.20)) !important;
  }
  html[data-theme] .chat-history-panel .chat-history-header{
    display:contents !important;
  }
  html[data-theme] .chat-history-panel .chat-history-title{
    order:1; flex:0 0 auto; font-size:15px !important;
  }
  html[data-theme] .chat-history-panel .chat-history-subbar{
    order:2; flex:1 1 auto; min-width:0;
    padding:0 !important; border-bottom:0 !important;
    justify-content:center !important;
  }
  html[data-theme] .chat-history-panel .chf-feedback{
    order:3; flex:0 0 auto; font-size:11.5px !important; padding:7px 10px !important;
  }
  /* フィルタも1行に収まるよう詰める */
  html[data-theme] .chat-history-panel .chf-btn{
    font-size:10.5px !important; padding:5px 8px !important;
  }
  /* 極小幅では評価ボタンのラベルを畳んでアイコンだけにする(はみ出し防止) */
  @media (max-width: 380px){
    html[data-theme] .chat-history-panel .chf-feedback-label{ display:none !important; }
    html[data-theme] .chat-history-panel .chf-feedback{ padding:7px 9px !important; }
  }
}

/* 縦スマホ: AIカメラは小さめの「テーマカード」(全画面だと映像だけでテーマが
   見えないため)。周囲にテーマ背景+装飾が露出し、枠はアクセントの発光リング。 */
@media (max-width: 700px) and (min-height: 541px){
  html[data-theme] #largeVideoContainer{
    left:calc(var(--dai-gap) + 6px) !important;
    right:calc(var(--dai-gap) + 6px) !important;
    top:calc(var(--dai-top) + var(--dai-gap) + 6px) !important;
    bottom:auto !important;
    height:42vh !important;
    border-radius:20px !important;
    box-shadow:0 0 0 2px var(--accent), 0 22px 55px -22px var(--glow) !important;
  }
  /* 自分カメラはカード右上に載せる */
  html[data-theme] #filmstripLocalVideo{
    top:calc(var(--dai-top) + var(--dai-gap) + 14px) !important;
    right:calc(var(--dai-gap) + 14px) !important;
  }
  /* 会話履歴 = AIカード下の余白に常設パネル(オーバーレイではなく埋め込み)。
     カード下端(top+gap+6+42vh)の少し下から、FAB行の上まで。 */
  html[data-theme] .chat-history-panel{
    left:var(--dai-gap) !important; right:var(--dai-gap) !important; width:auto !important;
    top:calc(var(--dai-top) + var(--dai-gap) + 42vh + 22px) !important;
    bottom:84px !important;
    height:auto !important; max-height:none !important;
    border-radius:16px !important; z-index:2 !important;
  }
}

/* 横スマホ: 会話履歴 = 右サイド(高さが無いため) */
@media (max-height: 540px){
  html[data-theme] .chat-history-panel{
    left:auto !important; right:var(--dai-gap) !important; width:46vw !important; max-width:340px !important;
    top:calc(var(--dai-top) + var(--dai-gap)) !important; bottom:88px !important;
    height:auto !important; max-height:none !important;
    border-radius:16px !important; z-index:300 !important;
  }
  /* パネル幅が最大340pxしかないので、1行ヘッダーは評価ボタンをアイコンだけに畳む */
  html[data-theme] .chat-history-panel .chf-feedback-label{ display:none !important; }
  html[data-theme] .chat-history-panel .chf-feedback{ padding:7px 9px !important; }
}
`;
